import React, { useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Box, IconButton, Grid, Button, Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import PrintIcon from '@mui/icons-material/Print';
import { useReactToPrint } from 'react-to-print';

const BillingDetailsModal = ({ open, onClose, billingData, planData, payAdvice, payGenerate, pendingDetails, summaryReport, messPlan, transPlan, receiptSummary }) => {

  const contentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({ contentRef });

  // Early return if no data
  if (!billingData || !billingData.TrnBookingList || billingData.TrnBookingList.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Billing Details</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>No billing data available</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  const bookingData = billingData?.TrnBookingList[0];
  const PlanData = planData?.[0];
  const PayAdvice = payAdvice;
  const PayGenerate = payGenerate;
  const PendingDetails = pendingDetails;
  const SummaryReports = summaryReport;

  const addonTotal = (SummaryReports?.[0]?.MessChrg || 0) + (SummaryReports?.[0]?.TrspChrg || 0);

  const bookingStatusMap = {
    O: { label: "Occupied", color: "#C62828" },
    C: { label: "Confirmed", color: "#2E7D32" },
    L: { label: "Checked Out", color: "#616161" },
    B: { label: "Booked", color: "#1565C0" },
  };

  const bookingStatus = bookingStatusMap[bookingData.BookSt] || {
    label: "-",
    color: "#000000",
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return dateString ? moment(dateString).format('DD-MM-YYYY') : 'N/A';
  };

  const sectionPrintStyle = {
    display: 'block',
    '@media print': {
      breakInside: 'avoid',
      pageBreakInside: 'avoid',
      paddingBottom: '6px',
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          backgroundColor: '#fff',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
          <Tooltip title='Print the statement' arrow>
            <Button
              variant="outlined"
              onClick={() => reactToPrintFn()}
              size='small'
            >
              <PrintIcon />
            </Button>
          </Tooltip>

          <IconButton onClick={onClose}>
            <CloseIcon color='error' />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box ref={contentRef}>
        <Typography variant="h6" sx={{ flexGrow: 1, textAlign: 'center', fontSize: '1.5rem', fontWeight: 'bold', mb: -2 }}>
          Account Statement
        </Typography>
        <DialogContent>
          <Box mb={0.3} p={0.2} border="1px solid #e0e0e0" borderRadius={1}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ borderBottom: '2px solid #635bff', paddingBottom: 0, marginBottom: '0px', fontSize: '1rem', fontWeight: 'bold' }}>
              Resident Info
            </Typography>
            <Grid container spacing={2}
              sx={{
                '@media print': {
                  display: 'flex',
                  flexWrap: 'nowrap',
                },
              }}
            >
              <Grid item xs={12} md={4}
                sx={{
                  '@media print': {
                    flex: '0 0 33.33%',
                    maxWidth: '33.33%',
                  },
                }}
              >
                <Box mb={0.3} mt={1}>
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}><strong>Name:</strong> {bookingData?.ResidentName}</Typography>
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}><strong>Mobile:</strong> {bookingData?.MobileNo}</Typography>
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}><strong>Email:</strong> {bookingData?.Email}</Typography>
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}><strong>Address:</strong> {bookingData?.AreaName}, {bookingData?.PinCode}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}
                sx={{
                  '@media print': {
                    flex: '0 0 33.33%',
                    maxWidth: '33.33%',
                  },
                }}
              >
                <Box mb={1} mt={1}>
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}><strong>City:</strong> {bookingData?.CityName}</Typography>
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}><strong>State:</strong> {bookingData?.StateName}</Typography>
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}><strong>Book Remark:</strong> {bookingData?.BookRemark}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box mb={1} mt={1}>
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}><strong>BookSt: </strong>
                    <span style={{ color: bookingStatus.color, fontWeight: 700 }}>
                      {bookingStatus.label}
                    </span>
                  </Typography>
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}><strong>Coupon No:</strong> {bookingData?.CouponNo}</Typography>
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}><strong>Topup Amount:</strong> {bookingData?.TopupAmt}</Typography>
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}><strong>Topup Days:</strong> {bookingData?.Topupdays}</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Booking Information */}
          <Box mb={0.5}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ borderBottom: '2px solid #635bff', paddingBottom: 0, fontSize: '1rem', fontWeight: 'bold' }}>
              Booking Info
            </Typography>
            <TableContainer component={Paper} sx={{ maxHeight: 340 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>BookNo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>BookRef</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>BookDate</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>CheckinDt</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>CheckoutDt</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>Addon1</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>Addon2</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow sx={{
                    '&:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                    height: '16px'
                  }}>
                    <TableCell sx={{ padding: '2px 5px' }}>{bookingData?.BookNo}</TableCell>
                    <TableCell sx={{ padding: '2px 5px' }}>{bookingData?.BookRef}</TableCell>
                    <TableCell sx={{ padding: '2px 5px' }}>{bookingData?.Duration}</TableCell>
                    <TableCell sx={{ padding: '2px 5px' }}>{formatDate(bookingData?.BookDtFrom)}</TableCell>
                    <TableCell sx={{ padding: '2px 5px' }}>{formatDate(bookingData?.CheckinDt)}</TableCell>
                    <TableCell sx={{ padding: '2px 5px' }}>{formatDate(bookingData?.BookUpToDt)}</TableCell>
                    <TableCell sx={{ padding: '2px 5px' }}>{transPlan?.TransPlanName}</TableCell>
                    <TableCell sx={{ padding: '2px 5px' }}>{messPlan?.MessPlanName}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Room Details Info */}
          <Box mb={0.5}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ borderBottom: '2px solid #635bff', paddingBottom: 0, fontSize: '1rem', fontWeight: 'bold' }}>
              Room Info
            </Typography>
            <TableContainer component={Paper} sx={{ maxHeight: 340 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>Property</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>Wing</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>Floor</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>RoomNo/BedNo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>RoomType</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>BedType</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>StdRent</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow sx={{
                    '&:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                    height: '16px'  // Reduce height of data rows
                  }}>
                    <TableCell sx={{ padding: '2px 16px' }}>{bookingData?.PropName}</TableCell>
                    <TableCell sx={{ padding: '2px 16px' }}>{bookingData?.WingName}</TableCell>
                    <TableCell sx={{ padding: '2px 16px' }}>{bookingData?.FloorName}</TableCell>
                    <TableCell sx={{ padding: '2px 16px' }}>{bookingData?.RoombedName}</TableCell>
                    <TableCell sx={{ padding: '2px 16px' }}>{bookingData?.RoomTypeName}</TableCell>
                    <TableCell sx={{ padding: '2px 16px' }}>{bookingData?.RoomBedTypeName}</TableCell>
                    <TableCell sx={{ padding: '2px 16px' }}>{bookingData?.TrnBookingDtlEntities?.[0]?.StdRent || 'N/A'}</TableCell>
                  </TableRow>
                  {/* ))} */}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Plan Details Info */}
          <Box mb={0.5}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ borderBottom: '2px solid #635bff', paddingBottom: 0, fontSize: '1rem', fontWeight: 'bold' }}>
              Payment Plan Info
            </Typography>
            <TableContainer component={Paper} sx={{ maxHeight: 340 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 10px', height: '32px', lineHeight: 0.5 }}>PaymentPlan</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 10px', height: '32px', lineHeight: 0.5 }}>Tenur</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 10px', height: '32px', lineHeight: 0.5 }}>FrMth</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 10px', height: '32px', lineHeight: 0.5 }}>LstAdj</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 10px', height: '32px', lineHeight: 0.5 }}>MinAdj</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 10px', height: '32px', lineHeight: 0.5 }}>BalChrg</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 10px', height: '32px', lineHeight: 0.5 }}>Deposit</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 10px', height: '32px', lineHeight: 0.5 }}>BillCycle</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 10px', height: '32px', lineHeight: 0.5 }}>NoOfPytAdv</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 10px', height: '32px', lineHeight: 0.5 }}>ExtraIncr%</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* {bookingData.TrnBookingDtlEntities?.map((row, index) => ( */}
                  <TableRow sx={{
                    '&:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                    height: '16px'  // Reduce height of data rows
                  }}>
                    <TableCell sx={{ padding: '2px 10px' }}>{PlanData?.PlanName}</TableCell>
                    <TableCell sx={{ padding: '2px 10px' }}>{PlanData?.PlanTenur}</TableCell>
                    <TableCell sx={{ padding: '2px 10px' }}>{PlanData?.FreeMonth}</TableCell>
                    <TableCell sx={{ padding: '2px 10px' }}>{PlanData?.LastAdjMonths}</TableCell>
                    <TableCell sx={{ padding: '2px 10px' }}>{PlanData?.MinAdjMonths}</TableCell>
                    <TableCell sx={{ padding: '2px 10px' }}>{PlanData?.BalChargMonth}</TableCell>
                    <TableCell sx={{ padding: '2px 10px' }}>{PlanData?.DepositAmt}</TableCell>
                    <TableCell sx={{ padding: '2px 10px' }}>{bookingData?.BillCycle}</TableCell>
                    <TableCell sx={{ padding: '2px 10px' }}>{PlanData?.BillMonthCycl}</TableCell>
                    <TableCell sx={{ padding: '2px 10px' }}>{PlanData?.IncRentPerc}</TableCell>
                  </TableRow>
                  {/* ))} */}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Billing Table */}
          <Box mb={0.5}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ borderBottom: '2px solid #635bff', paddingBottom: 0, fontSize: '1rem', fontWeight: 'bold' }}>
              Rent Summary
            </Typography>
            <TableContainer component={Paper} sx={{ maxHeight: 340 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>BillDate</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>Month/Year</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>StdRent</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>ExtraRent</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>Addon1</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>Addon2</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>AdvAmt</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>NetRent</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>DueDate</TableCell>
                    {/* <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', padding: '6px 16px', height: '32px' }}>Due Amount</TableCell> */}
                    {/* <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', padding: '6px 16px', height: '32px' }}>Balance</TableCell> */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookingData?.TrnBookingDtlEntities?.map((row, index) => (
                    <TableRow key={index} sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                      height: '16px'
                    }}>
                      <TableCell sx={{ padding: '2px 5px' }}>{formatDate(row.BillDt)}</TableCell>
                      <TableCell sx={{ padding: '2px 5px' }}>{`${row.MonthNm} ${row.YearNm}`}</TableCell>
                      <TableCell sx={{ padding: '2px 5px' }}>{formatCurrency(row.StdRent)}</TableCell>
                      <TableCell sx={{ padding: '2px 5px' }}>{formatCurrency(row.ExtraRent)}</TableCell>
                      <TableCell sx={{ padding: '2px 5px' }}>{formatCurrency(row.TrspChrg)}</TableCell>
                      <TableCell sx={{ padding: '2px 5px' }}>{formatCurrency(row.MessChrg)}</TableCell>
                      <TableCell sx={{ padding: '2px 5px' }}>{formatCurrency(row.EffRent)}</TableCell>
                      <TableCell sx={{ padding: '2px 5px' }}>{formatCurrency(row.PyadAmt)}</TableCell>
                      <TableCell sx={{ padding: '2px 5px' }}>{formatDate(row.PyadDt)}</TableCell>
                      {/* <TableCell sx={{ padding: '2px 16px' }}>{formatCurrency(row.DueAmt)}</TableCell> */}
                      {/* <TableCell sx={{ padding: '2px 16px' }}>{formatCurrency(row.BalPyadAmt)}</TableCell> */}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Summary Section */}
          {summaryReport?.length > 0 &&
            <Box mb={0.5} sx={sectionPrintStyle}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ borderBottom: '2px solid #635bff', paddingBottom: 0, fontSize: '1rem', fontWeight: 'bold' }}>
                Summary
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 340 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>Rent</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>Discount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>Addon</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>Payable</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow >
                      <TableCell sx={{ padding: '2px 16px' }}> {formatCurrency(SummaryReports?.[0]?.BedRent)}</TableCell>
                      <TableCell sx={{ padding: '2px 16px' }}> {formatCurrency(SummaryReports?.[0]?.DiscAmt)}</TableCell>
                      <TableCell sx={{ padding: '2px 16px' }}> {formatCurrency(SummaryReports?.[0]?.NetAmt)}</TableCell>
                      <TableCell sx={{ padding: '2px 16px' }}> {formatCurrency(addonTotal)}</TableCell>
                      <TableCell sx={{ padding: '2px 16px' }}> {formatCurrency(SummaryReports?.[0]?.TotalRent)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          }

          {/* Payment Advice Info */}
          {PayAdvice?.length > 0 &&
            <Box mb={0.5} sx={sectionPrintStyle}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ borderBottom: '2px solid #635bff', paddingBottom: 0, fontSize: '1rem', fontWeight: 'bold' }}>
                Installment Details
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 340 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>AdvDate</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>AdviceFor</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>BalAmt</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>PytCount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>DueAmt</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>DueDt</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {PayAdvice?.map((PayAdvice, index) => (
                      <TableRow key={index} sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                        height: '16px'
                      }}>
                        <TableCell sx={{ padding: '2px 16px' }}>{formatDate(PayAdvice?.PytAdvDt)}</TableCell>
                        <TableCell sx={{ padding: '2px 16px' }}>
                          {PayAdvice?.PytAdvFor === "D" ? "Deposit" : PayAdvice?.PytAdvFor === "R" ? "Rent" : PayAdvice?.PytAdvFor === "T" ? "TopUp" : ""}
                        </TableCell>
                        <TableCell sx={{ padding: '2px 16px' }}>{PayAdvice?.Amount}</TableCell>
                        <TableCell sx={{ padding: '2px 16px' }}>{PayAdvice?.BalAmount}</TableCell>
                        <TableCell sx={{ padding: '2px 16px' }}>{PayAdvice?.BookPytAdvNo}</TableCell>
                        <TableCell sx={{ padding: '2px 16px' }}>{PayAdvice?.DueAmt}</TableCell>
                        <TableCell sx={{ padding: '2px 16px' }}>{formatDate(PayAdvice?.DueDt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          }

          {/* Account Generate Info */}
          {PayGenerate?.length > 0 &&
            <Box mb={0.5} sx={sectionPrintStyle}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ borderBottom: '2px solid #635bff', paddingBottom: 0, fontSize: '1rem', fontWeight: 'bold' }}>
                Billed Advice
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 340 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>AdvNo</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>AdvDt</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>Advice For</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>AdvAmt</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>RcdAmt</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>Balance</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>RecNo</TableCell>
                      {/* <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px' }}>Rec Date</TableCell> */}
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {PayGenerate?.map((PayGenerate, index) => (
                      <TableRow key={index} sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                        height: '16px'
                      }}>
                        <TableCell sx={{ padding: '2px 16px' }}>{PayGenerate?.PytAdvNo}</TableCell>
                        <TableCell sx={{ padding: '2px 16px' }}>{formatDate(PayGenerate?.PytAdvDt)}</TableCell>
                        {/* <TableCell sx={{ padding: '2px 16px' }}>
                        {PayGenerate?.PytAdvFor === "D" ? "Deposit" : PayGenerate?.PytAdvFor === "R" ? "Rent" : PayGenerate?.PytAdvFor === "O" ? "Other" : PayGenerate?.PytAdvFor === "T" ? "TopUp" : ""}
                      </TableCell> */}
                        <TableCell sx={{ padding: '2px 16px' }}>{PayGenerate?.Remark}</TableCell>
                        <TableCell sx={{ padding: '2px 16px' }}>{PayGenerate?.Amount}</TableCell>
                        <TableCell sx={{ padding: '2px 16px' }}>{PayGenerate?.RcdAmt}</TableCell>
                        <TableCell sx={{ padding: '2px 16px' }}>{PayGenerate?.BalAmount}</TableCell>
                        <TableCell sx={{ padding: '2px 16px' }}>{PayGenerate?.BookRef}</TableCell>
                        {/* <TableCell sx={{ padding: '2px 16px' }}>{formatDate(PayGenerate?.RecDt)}</TableCell> */}
                        <TableCell
                          sx={{
                            padding: '2px 16px',
                            backgroundColor: PayGenerate?.IsDepPaid === "Y" ? 'green' : 'red',
                            width: '0px',
                            textAlign: 'center',
                            borderRadius: '8px',
                            color: 'white'
                          }}
                        >
                          {PayGenerate?.IsDepPaid === "Y" ? "Paid" : (PayGenerate?.IsDepPaid === "N" || PayGenerate?.IsDepPaid === null || PayGenerate?.IsDepPaid === "") ? "Unpaid" : ""}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          }

          {/* Pending Advice Info */}
          {pendingDetails?.length > 0 &&
            <Box mb={0.5} sx={sectionPrintStyle}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ borderBottom: '2px solid #635bff', paddingBottom: 0, fontSize: '1rem', fontWeight: 'bold' }}>
                Unbilled Advice
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 340 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>BookNo</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>AdvDt</TableCell>
                      {/* <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px' }}>Unit</TableCell> */}
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>AdvAmt</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>BalAmt</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>DueDays</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>DueDate</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '6px 16px', height: '32px', lineHeight: 0.5 }}>Remark</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {PendingDetails?.map((PendingDetails, index) => (
                      <TableRow key={index} sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                        height: '16px'
                      }}>
                        <TableCell sx={{ padding: '2px 16px' }}>{PendingDetails?.BookNo}</TableCell>
                        <TableCell sx={{ padding: '2px 16px' }}>{formatDate(PendingDetails?.DueDt)}</TableCell>
                        {/* <TableCell sx={{ padding: '2px 16px' }}>{PendingDetails?.RoombedName}</TableCell> */}
                        <TableCell sx={{ padding: '2px 16px' }}>{PendingDetails?.Amount}</TableCell>
                        <TableCell sx={{ padding: '2px 16px' }}>{PendingDetails?.BalAmount}</TableCell>
                        <TableCell sx={{ padding: '2px 16px' }}>{PendingDetails?.DueDays}</TableCell>
                        <TableCell sx={{ padding: '2px 16px' }}>{formatDate(PendingDetails?.DueDt)}</TableCell>
                        <TableCell sx={{ padding: '2px 16px' }}>{PendingDetails?.Remark}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          }
          {receiptSummary?.length > 0 &&
            <Box mb={0.5} sx={sectionPrintStyle}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ borderBottom: '2px solid #635bff', paddingBottom: 0, fontSize: '1rem', fontWeight: 'bold' }}>
                Receipt Summary
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 340 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>RecNo</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>RecDt</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>PytAdvNo</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>RecAmt</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>PaymentFor</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>Mode</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>TrnNo/ChqNo</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>TrnDt</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FFFFFF', padding: '2px 5px', height: '32px', lineHeight: 0.5 }}>Remark</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {receiptSummary?.filter(receipt => receipt?.RecNo)
                      .map((receipt, index) => (
                        <TableRow key={index} sx={{
                          '&:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                          height: '16px'
                        }}>
                          <TableCell sx={{ padding: '2px 5px' }}>{receipt?.RecNo}</TableCell>
                          <TableCell sx={{ padding: '2px 5px' }}>{formatDate(receipt?.RecDt)}</TableCell>
                          <TableCell sx={{ padding: '2px 5px' }}>{receipt?.PytAdvNo}</TableCell>
                          <TableCell sx={{ padding: '2px 5px' }}>{receipt?.Amount}</TableCell>
                          <TableCell sx={{ padding: '2px 5px' }}>{receipt?.InstallmentNo}</TableCell>
                          <TableCell sx={{ padding: '2px 5px' }}>{receipt?.Flag}</TableCell>
                          <TableCell sx={{ padding: '2px 5px' }}>{receipt?.Chqno}</TableCell>
                          <TableCell sx={{ padding: '2px 5px' }}> {receipt?.ChqDt ? moment(receipt.ChqDt).format('DD-MM-YYYY') : ''}</TableCell>
                          <TableCell sx={{ padding: '2px 5px' }}>{receipt?.SearchText}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          }
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default BillingDetailsModal;